import { useRef, useEffect } from 'react';
import { getObserver, cleanupObservers } from './observerService';
import { useAnimationsEnabled } from '../AnimationContext';

export const useGlowEffect = () => {
    const animationsEnabled = useAnimationsEnabled();

    const mousePos = useRef({ x: 0, y: 0 });
    const animationRef = useRef();
    const visibleElements = useRef(new Map());
    const isActive = useRef(true);

    useEffect(() => {
        if (!animationsEnabled) return;

        isActive.current = true;

        const glowSelector =
            '.gradient-text, .gradient-border, .gradient-background, #loading-spinner';

        const refreshRects = () => {
            visibleElements.current.forEach(data => {
                data.rect = data.element.getBoundingClientRect();
            });
        };

        const measureGlow = (data, x, y) => {
            const { element, rect, isText } = data;

            const posX = Math.max(
                0,
                Math.min(100, ((x - rect.left) / rect.width) * 100)
            );

            const posY = Math.max(
                0,
                Math.min(100, ((y - rect.top) / rect.height) * 100)
            );

            const elementSize = Math.max(rect.width, rect.height);

            const maxDistance = isText
                ? 100 + elementSize * 0.5
                : 40 + elementSize * 0.15;

            let distance;

            if (isText) {
                const centerX = rect.left + rect.width / 2;
                const centerY = rect.top + rect.height / 2;

                distance = Math.sqrt(
                    (x - centerX) ** 2 +
                    (y - centerY) ** 2
                );

                distance = Math.max(
                    0,
                    distance - Math.min(rect.width, rect.height) / 2
                );
            } else {
                const dx =
                    x < rect.left
                        ? rect.left - x
                        : x > rect.right
                            ? x - rect.right
                            : 0;

                const dy =
                    y < rect.top
                        ? rect.top - y
                        : y > rect.bottom
                            ? y - rect.bottom
                            : 0;

                distance = Math.sqrt(dx * dx + dy * dy);
            }

            const intensity =
                distance >= maxDistance
                    ? 0
                    : 1 - distance / maxDistance;

            return {
                element,
                data,
                posX,
                posY,
                intensity,
            };
        };

        const applyGlow = ({
            element,
            data,
            posX,
            posY,
            intensity,
        }) => {
            if (
                data.lastX !== null &&
                Math.abs(data.lastX - posX) < 0.25 &&
                Math.abs(data.lastY - posY) < 0.25 &&
                Math.abs(data.lastIntensity - intensity) < 0.01
            ) {
                return;
            }

            element.style.setProperty('--gradient-x', `${posX}%`);
            element.style.setProperty('--gradient-y', `${posY}%`);
            element.style.setProperty('--gradient-stop', `${20 * intensity}%`);
            element.style.setProperty('--gradient-start', `${60 * intensity}%`);
            element.style.setProperty(
                '--glow-intensity',
                intensity.toFixed(3)
            );

            data.lastX = posX;
            data.lastY = posY;
            data.lastIntensity = intensity;
        };

        const updateVisuals = () => {
            if (!isActive.current) return;

            const { x, y } = mousePos.current;
            const pendingUpdates = [];

            visibleElements.current.forEach(data => {
                pendingUpdates.push(measureGlow(data, x, y));
            });

            pendingUpdates.forEach(applyGlow);

            animationRef.current = undefined;
        };

        const scheduleUpdate = () => {
            if (
                !isActive.current ||
                animationRef.current ||
                visibleElements.current.size === 0
            ) {
                return;
            }

            animationRef.current = requestAnimationFrame(updateVisuals);
        };

        const observer = getObserver(
            'glow-effect',
            entries => {
                let needsUpdate = false;

                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const element = entry.target;

                        visibleElements.current.set(element, {
                            element,
                            rect: element.getBoundingClientRect(),
                            isText:
                                element.classList.contains('gradient-text') ||
                                element.classList.contains('gradient-background'),
                            lastX: null,
                            lastY: null,
                            lastIntensity: null,
                        });

                        needsUpdate = true;
                    } else {
                        visibleElements.current.delete(entry.target);
                    }
                });

                if (needsUpdate) {
                    scheduleUpdate();
                }
            },
            {
                rootMargin: '100px',
            }
        );

        const observeElements = () => {
            document
                .querySelectorAll(glowSelector)
                .forEach(el => observer.observe(el));
        };

        observeElements();

        const mutationObserver = new MutationObserver(mutations => {
            const shouldObserve = mutations.some(m =>
                Array.from(m.addedNodes).some(
                    node =>
                        node.nodeType === 1 &&
                        (
                            node.classList?.contains('gradient-text') ||
                            node.classList?.contains('gradient-border') ||
                            node.classList?.contains('gradient-background') ||
                            node.id === 'loading-spinner' ||
                            node.querySelectorAll?.(glowSelector).length
                        )
                )
            );

            if (shouldObserve) {
                requestAnimationFrame(() => {
                    observeElements();
                    scheduleUpdate();
                });
            }
        });

        mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
        });

        const trackMouse = e => {
            mousePos.current.x = e.clientX;
            mousePos.current.y = e.clientY;

            scheduleUpdate();
        };

        const handleLayoutChange = () => {
            refreshRects();
            scheduleUpdate();
        };

        window.addEventListener('mousemove', trackMouse, {
            passive: true,
        });

        window.addEventListener('scroll', handleLayoutChange, {
            passive: true,
        });

        window.addEventListener('resize', handleLayoutChange);

        scheduleUpdate();

        return () => {
            isActive.current = false;

            window.removeEventListener('mousemove', trackMouse);
            window.removeEventListener('scroll', handleLayoutChange);
            window.removeEventListener('resize', handleLayoutChange);

            mutationObserver.disconnect();

            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }

            cleanupObservers('glow-effect');
        };
    }, [animationsEnabled]);
};
